import React, { useState } from 'react';
import { X, Zap, Star, CheckCircle2, Activity, Rocket, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const loadRazorpay = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PLANS = [
  {
    id: 'starter',
    name: 'Starter Plan',
    icon: <Rocket size={20} color="#10B981" />,
    monthlyPrice: 29,
    yearlyPrice: 24,
    color: '#10B981',
    desc: 'Perfect for freelancers & solo businesses',
    features: [
      { text: 'Up to 50 Invoices/month', included: true },
      { text: '5 Customers', included: true },
      { text: 'PDF Export', included: true },
      { text: 'Email Support', included: true },
      { text: 'Custom Branding', included: false },
      { text: 'Analytics & Reports', included: false },
    ],
  },
  {
    id: 'professional',
    name: 'Professional Plan',
    icon: <Zap size={20} color="#0793A7" />,
    monthlyPrice: 69,
    yearlyPrice: 57,
    color: '#0793A7',
    popular: true,
    desc: 'For growing businesses & teams',
    features: [
      { text: 'Unlimited Invoices', included: true },
      { text: 'Unlimited Customers', included: true },
      { text: 'PDF & Excel Export', included: true },
      { text: 'Priority Support', included: true },
      { text: 'Custom Branding & Logo', included: true },
      { text: 'Analytics & Reports', included: true },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise Plan',
    icon: <Building2 size={20} color="#7c3aed" />,
    monthlyPrice: 149,
    yearlyPrice: 124,
    color: '#7c3aed',
    desc: 'For large teams & enterprises',
    features: [
      { text: 'Unlimited Invoices', included: true },
      { text: 'Unlimited Customers', included: true },
      { text: 'PDF, Excel & CSV Export', included: true },
      { text: 'Dedicated Support', included: true },
      { text: 'White-label Branding', included: true },
      { text: 'Advanced Analytics', included: true },
    ],
  },
];

const FEATURE_LABELS = {
  invoiceLimit: 'Create More Invoices',
  customerLimit: 'Add More Customers',
  excelExport: 'Excel Export',
  csvExport: 'CSV & Backup Export',
  reports: 'Analytics & Reports',
  customBranding: 'Custom Branding',
  advancedAnalytics: 'Advanced Analytics',
  whitelabelBranding: 'White-label Branding',
  renew: 'Renew Subscription'
};

const UpgradeModal = ({ isOpen, onClose, feature, requiredPlan, message }) => {
  const { user } = useAuth();
  const [currentPlanIndex, setCurrentPlanIndex] = useState(() => {
    const id = requiredPlan === 'enterprise' ? 'enterprise' : 'professional';
    const index = PLANS.findIndex(p => p.id === id);
    return index >= 0 ? index : 1;
  });
  const [billing, setBilling] = useState('monthly');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const selectedPlan = PLANS[currentPlanIndex] || PLANS[1];
  const featureLabel = FEATURE_LABELS[feature] || 'This Feature';
  const isHardLock = feature === 'renew';
  const price = billing === 'yearly' ? selectedPlan.yearlyPrice : selectedPlan.monthlyPrice;

  const handlePayment = async () => {
    setErrorMsg('');
    if (!confirmEmail) {
      setErrorMsg('Please confirm your email address.');
      return;
    }
    if (confirmEmail !== user?.email) {
      setErrorMsg('Email does not match your account email.');
      return;
    }

    setLoading(true);
    const res = await loadRazorpay();
    if (!res) {
      setErrorMsg('Razorpay SDK failed to load. Are you online?');
      setLoading(false);
      return;
    }

    try {
      const orderResponse = await api.post('/payment/create-order', {
        amount: price,
        planType: selectedPlan.id
      });

      const orderData = orderResponse.data;
      if (!orderData.success) {
        setErrorMsg('Server error. Could not create order.');
        setLoading(false);
        return;
      }

      const options = {
        key: 'rzp_test_T6h4R2DXHkW5Ow', 
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'FastInvoice.in',
        description: `Subscription for ${selectedPlan.name}`,
        order_id: orderData.order.id,
        handler: async function (response) {
          try {
            const verifyRes = await api.post('/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user: {
                name: user.name,
                email: user.email,
                planType: selectedPlan.id,
                billingCycle: billing,
                isUpgrade: true
              }
            });

            if (verifyRes.data.success) {
              alert('Payment successful! Your plan is now upgraded.');
              window.location.reload();
            } else {
              alert(verifyRes.data.message || 'Payment verification failed!');
            }
          } catch (err) {
            alert('Error during verification.');
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || ''
        },
        theme: {
          color: selectedPlan.color
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.on('payment.failed', function (response) {
        alert('Payment Failed! ' + response.error.description);
      });
      paymentObject.open();

    } catch (err) {
      setErrorMsg('Something went wrong initiating payment!');
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 999999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(8px)',
        animation: 'fadeIn 0.2s ease',
        padding: '20px'
      }}
      onMouseDown={(e) => { if (!isHardLock && e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#f8fafc',
        borderRadius: '20px',
        padding: '0',
        maxWidth: '540px',
        width: '100%',
        maxHeight: '100vh',
        overflow: 'hidden',
        boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
        animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header Area */}
        <div style={{
          background: 'linear-gradient(135deg, #0793A7 0%, #05697a 100%)',
          padding: '20px 30px',
          position: 'relative',
          color: '#fff',
          textAlign: 'center'
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'rgba(255,255,255,0.2)', border: 'none',
              borderRadius: '50%', width: '32px', height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff', transition: 'background 0.2s', zIndex: 10
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.35)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            <X size={16} />
          </button>

          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', marginBottom: '12px' }}>
            <Zap size={20} fill="white" color="white" />
          </div>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px', opacity: 0.9 }}>
            Upgrade Required
          </div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800' }}>
            Unlock {featureLabel}
          </h2>
          {message && <p style={{ margin: '8px 0 0 0', opacity: 0.9, fontSize: '14px', lineHeight: '1.4' }}>{message}</p>}
        </div>

        {/* Body Area */}
        <div style={{ padding: '20px 30px', background: '#fff', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
          
          {/* Billing Toggle */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <div style={{ background: '#f1f5f9', padding: '4px', borderRadius: '12px', display: 'inline-flex' }}>
              <button
                onClick={() => setBilling('monthly')}
                style={{
                  padding: '8px 24px', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', border: 'none',
                  background: billing === 'monthly' ? '#fff' : 'transparent',
                  color: billing === 'monthly' ? '#0f172a' : '#64748b',
                  boxShadow: billing === 'monthly' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling('yearly')}
                style={{
                  padding: '8px 24px', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', border: 'none',
                  background: billing === 'yearly' ? '#fff' : 'transparent',
                  color: billing === 'yearly' ? '#0f172a' : '#64748b',
                  boxShadow: billing === 'yearly' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', gap: '8px'
                }}
              >
                Yearly
                <span style={{ fontSize: '11px', background: '#10B981', color: '#fff', padding: '2px 8px', borderRadius: '20px' }}>Save 17%</span>
              </button>
            </div>
          </div>

          {/* Carousel Area */}
          <div style={{ position: 'relative', width: '320px', margin: '0 auto 20px auto' }}>
            <button 
              onClick={() => setCurrentPlanIndex(i => Math.max(0, i - 1))}
              disabled={currentPlanIndex === 0}
              style={{
                position: 'absolute', left: '-50px', top: '50%', transform: 'translateY(-50%)', zIndex: 10,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', cursor: currentPlanIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: currentPlanIndex === 0 ? 0.3 : 1, width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'all 0.2s'
              }}
            >
              <ChevronLeft size={24} color="#0f172a" />
            </button>

            <div style={{ width: '320px' }}>
              {(() => {
                const p = PLANS[currentPlanIndex];
                const displayPrice = billing === 'yearly' ? p.yearlyPrice : p.monthlyPrice;
                
                return (
                  <div 
                    key={p.id}
                    style={{
                      width: '320px',
                      position: 'relative',
                      border: `2px solid ${p.color}`,
                      background: `${p.color}05`,
                      borderRadius: '16px',
                      padding: '24px 24px 16px 24px',
                      boxShadow: `0 10px 30px ${p.color}20`,
                      animation: 'fadeIn 0.3s ease'
                    }}
                  >
                    {p.popular && (
                      <div style={{
                        position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                        background: '#ec4899', color: '#fff', fontSize: '11px', fontWeight: '700',
                        padding: '4px 12px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '4px'
                      }}>
                        <Star size={12} fill="white" /> Most Popular
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      {p.icon}
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{p.name}</h3>
                    </div>

                    <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'baseline' }}>
                      <span style={{ fontSize: '20px', fontWeight: '600', color: '#475569' }}>₹</span>
                      <span style={{ fontSize: '38px', fontWeight: '800', color: '#0f172a', letterSpacing: '-1px' }}>{displayPrice}</span>
                      <span style={{ fontSize: '14px', color: '#64748b', marginLeft: '4px' }}>/mo</span>
                    </div>

                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px', minHeight: '34px' }}>{p.desc}</p>

                    <ul style={{ listStyle: 'none', padding: 0, margin: '0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {p.features.map((f, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: f.included ? '#334155' : '#94a3b8' }}>
                          <div style={{ marginTop: '2px' }}>
                            <CheckCircle2 size={16} color={f.included ? p.color : '#cbd5e1'} />
                          </div>
                          <span style={{ textDecoration: f.included ? 'none' : 'line-through' }}>{f.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}
            </div>

            <button 
              onClick={() => setCurrentPlanIndex(i => Math.min(PLANS.length - 1, i + 1))}
              disabled={currentPlanIndex === PLANS.length - 1}
              style={{
                position: 'absolute', right: '-50px', top: '50%', transform: 'translateY(-50%)', zIndex: 10,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: '50%', cursor: currentPlanIndex === PLANS.length - 1 ? 'not-allowed' : 'pointer',
                opacity: currentPlanIndex === PLANS.length - 1 ? 0.3 : 1, width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'all 0.2s'
              }}
            >
              <ChevronRight size={24} color="#0f172a" />
            </button>
          </div>

          {/* Checkout Area */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px', width: '100%', maxWidth: '380px', margin: 'auto auto 0 auto' }}>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: '#1e293b' }}>Confirm Email Address</label>
              <input 
                type="email" 
                value={confirmEmail} 
                onChange={e => { setConfirmEmail(e.target.value); setErrorMsg(''); }} 
                placeholder="Enter your logged-in email" 
                style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: errorMsg ? '1px solid #ef4444' : '1px solid #cbd5e1', fontSize: '14px', outline: 'none', background: '#f8fafc' }}
              />
              {errorMsg && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', fontWeight: '500' }}>{errorMsg}</div>}
            </div>

            <button
              onClick={handlePayment}
              disabled={loading}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '14px 20px', borderRadius: '10px',
                background: selectedPlan.color, color: '#fff',
                border: 'none', fontWeight: '700', fontSize: '16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.2s, transform 0.2s',
                boxShadow: `0 8px 20px ${selectedPlan.color}40`,
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? (
                <><Activity size={18} className="anim-spin-slow" /> Processing...</>
              ) : (
                <>Pay ₹{price} Now</>
              )}
            </button>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .anim-spin-slow { animation: spin 2s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default UpgradeModal;
